/**
 * CRUD handlers for Organisation Management.
 *
 * Extracted from OrganisationManagementPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { createOrganisation, updateOrganisation, deleteOrganisation } from '../../services/admin'
import type { Organisation, OrganisationStatus } from '../../types'
import type { CreateOrgForm } from './types'
import { isValidUuid } from './helpers'
import { logger } from '../../utils/logger'

interface HandlerDeps {
  selectedOrg: Organisation | null
  formData: CreateOrgForm
  setFormLoading: (v: boolean) => void
  setFormError: (msg: string | null) => void
  setShowCreateModal: (v: boolean) => void
  setShowEditModal: (v: boolean) => void
  setShowDeleteModal: (v: boolean) => void
  setSelectedOrg: (org: Organisation | null) => void
  setActionMenuOrg: (id: string | null) => void
  resetForm: () => void
  loadData: () => Promise<void>
}

interface OrganisationHandlers {
  handleCreateOrg: (e: React.FormEvent) => Promise<void>
  handleUpdateOrg: (e: React.FormEvent) => Promise<void>
  handleDeleteOrg: () => Promise<void>
  handleToggleStatus: (org: Organisation) => Promise<void>
}

async function performCreate(deps: HandlerDeps): Promise<void> {
  await createOrganisation({
    name: deps.formData.name,
    slug: deps.formData.slug,
    orgType: deps.formData.orgType,
    xeroOrganisationId: deps.formData.xeroOrganisationId || null,
  })
  deps.setShowCreateModal(false)
  deps.resetForm()
  void deps.loadData()
}

async function performUpdate(deps: HandlerDeps): Promise<void> {
  if (!deps.selectedOrg) return
  const xeroOrgId = deps.formData.xeroOrganisationId || null
  const originalXeroOrgId = deps.selectedOrg.xeroOrganisationId ?? null
  const xeroOrgIdChanged = xeroOrgId !== originalXeroOrgId
  await updateOrganisation(deps.selectedOrg.id, {
    name: deps.formData.name,
    ...(xeroOrgIdChanged ? { xeroOrganisationId: xeroOrgId } : {}),
  })
  deps.setShowEditModal(false)
  deps.setSelectedOrg(null)
  deps.resetForm()
  void deps.loadData()
}

async function performDelete(deps: HandlerDeps): Promise<void> {
  if (!deps.selectedOrg) return
  await deleteOrganisation(deps.selectedOrg.id)
  deps.setShowDeleteModal(false)
  deps.setSelectedOrg(null)
  void deps.loadData()
}

async function performToggleStatus(deps: HandlerDeps, org: Organisation): Promise<void> {
  const newStatus: OrganisationStatus = org.status === 'active' ? 'suspended' : 'active'
  await updateOrganisation(org.id, { status: newStatus })
  void deps.loadData()
}

export function useOrganisationHandlers(deps: HandlerDeps): OrganisationHandlers {
  const { t } = useTranslation('admin')

  const xeroIdInvalid = (): boolean =>
    Boolean(deps.formData.xeroOrganisationId) && !isValidUuid(deps.formData.xeroOrganisationId)

  const runWithFormState = async (action: () => Promise<void>, fallback: string): Promise<void> => {
    deps.setFormLoading(true)
    deps.setFormError(null)
    if (xeroIdInvalid()) {
      deps.setFormError(t('organisations.errors.invalidXeroId'))
      deps.setFormLoading(false)
      return
    }
    try {
      await action()
    } catch (err: unknown) {
      const error = err as Error
      deps.setFormError(error.message || fallback)
    } finally {
      deps.setFormLoading(false)
    }
  }

  const handleCreateOrg = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    await runWithFormState(() => performCreate(deps), t('organisations.errors.createFailed'))
  }

  const handleUpdateOrg = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!deps.selectedOrg) return
    await runWithFormState(() => performUpdate(deps), t('organisations.errors.updateFailed'))
  }

  const handleDeleteOrg = async (): Promise<void> => {
    if (!deps.selectedOrg) return
    await runWithFormState(() => performDelete(deps), t('organisations.errors.deleteFailed'))
  }

  const handleToggleStatus = async (org: Organisation): Promise<void> => {
    try {
      await performToggleStatus(deps, org)
    } catch (err) {
      logger.error('useOrganisationHandlers', 'Failed to update organisation status', err)
    }
    deps.setActionMenuOrg(null)
  }

  return { handleCreateOrg, handleUpdateOrg, handleDeleteOrg, handleToggleStatus }
}
